import Vue from "vue";
import {
  apiRequest,
  clearCSRFToken,
  loadCSRFToken,
  saveCSRFToken,
} from "./api";

export const authState = Vue.observable({
  ready: false,
  busy: false,
  session: null,
  csrfToken: loadCSRFToken(),
});

function setSessionFromPayload(payload) {
  authState.session = payload && payload.session ? payload.session : null;
  authState.csrfToken = payload && payload.csrf_token ? payload.csrf_token : "";
  saveCSRFToken(authState.csrfToken);
}

function mergeSessionUser(user) {
  if (!(user && authState.session)) {
    return user || null;
  }
  authState.session = {
    ...authState.session,
    user: {
      ...(authState.session.user || {}),
      ...user,
    },
  };
  return authState.session.user;
}

export async function restoreSession(options = {}) {
  authState.busy = true;
  try {
    const payload = await apiRequest("/api/v1/auth/session", options);
    if (payload && payload.authenticated) {
      setSessionFromPayload(payload);
      return authState.session;
    }

    authState.session = null;
    authState.csrfToken = "";
    clearCSRFToken();
    return null;
  } catch (error) {
    if (error && error.name === "AbortError") {
      throw error;
    }
    authState.session = null;
    authState.csrfToken = "";
    clearCSRFToken();
    return null;
  } finally {
    authState.busy = false;
    authState.ready = true;
  }
}

export async function loginWithPassword(identifier, password, rememberMe = false) {
  const payload = await apiRequest("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password, remember_me: rememberMe }),
  });
  setSessionFromPayload(payload);
  return payload;
}

export async function sendSMSCode(phone, purpose, state = "") {
  return apiRequest("/api/v1/auth/sms/send", {
    method: "POST",
    body: JSON.stringify({ phone, purpose, ...(state ? { state } : {}) }),
  });
}

export async function loginWithSMS(phone, code, inviteCode = "") {
  const payload = await apiRequest("/api/v1/auth/login/sms", {
    method: "POST",
    body: JSON.stringify({ phone, code, invite_code: inviteCode }),
  });
  setSessionFromPayload(payload);
  return payload;
}

export async function resetPasswordBySMS(phone, code, newPassword) {
  return apiRequest("/api/v1/auth/password/reset/sms", {
    method: "POST",
    body: JSON.stringify({
      phone,
      code,
      new_password: newPassword,
    }),
  });
}

export async function fetchWechatQRCode() {
  const payload = await apiRequest("/api/v1/auth/wechat/qrcode");
  return payload && payload.data ? payload.data : null;
}

export async function checkWechatState(state, options = {}) {
  return apiRequest(`/api/v1/auth/wechat/check?state=${encodeURIComponent(state)}`, options);
}

export async function bindWechatPhone(state, phone, verifyCode, inviteCode = "", options = {}) {
  const payload = await apiRequest("/api/v1/auth/wechat/bind", {
    ...options,
    method: "POST",
    body: JSON.stringify({
      state,
      phone,
      verify_code: verifyCode,
      invite_code: inviteCode,
    }),
  });
  const session = await restoreSession(options);
  return { data: payload && payload.data ? payload.data : {}, session };
}

export async function fetchMyProfile() {
  const payload = await apiRequest("/api/v1/me");
  if (payload && payload.user) {
    mergeSessionUser(payload.user);
  }
  return payload && payload.user ? payload.user : null;
}

export async function updateMyProfile(input) {
  const payload = await apiRequest("/api/v1/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  if (payload && payload.user) {
    mergeSessionUser(payload.user);
  }
  return payload && payload.user ? payload.user : null;
}

export async function changeMyPassword(currentPassword, newPassword) {
  return apiRequest("/api/v1/auth/password/change", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
}

export async function sendEmailVerificationCode() {
  return apiRequest("/api/v1/auth/email/verify/send", {
    method: "POST",
  });
}

export async function confirmEmailVerificationCode(code) {
  return apiRequest("/api/v1/auth/email/verify/confirm", {
    method: "POST",
    body: JSON.stringify({ code: String(code || "").trim() }),
  });
}

export async function sendMyPhoneCode(step, phone = "") {
  return apiRequest("/api/v1/me/phone/code", {
    method: "POST",
    body: JSON.stringify({
      step,
      phone,
    }),
  });
}

export async function verifyMyCurrentPhoneCode(code) {
  return apiRequest("/api/v1/me/phone/current/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function updateMyPhone(input) {
  const payload = await apiRequest("/api/v1/me/phone", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  if (payload && payload.user) {
    mergeSessionUser(payload.user);
  }
  return payload && payload.user ? payload.user : null;
}

export async function sendMyEmailCode(step, email = "") {
  return apiRequest("/api/v1/me/email/code", {
    method: "POST",
    body: JSON.stringify({
      step,
      email,
    }),
  });
}

export async function updateMyEmail(input) {
  const payload = await apiRequest("/api/v1/me/email", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  if (payload && payload.user) {
    mergeSessionUser(payload.user);
  }
  return payload && payload.user ? payload.user : null;
}

export async function logout() {
  try {
    await apiRequest("/api/v1/auth/logout", {
      method: "POST",
    });
  } finally {
    authState.session = null;
    authState.csrfToken = "";
    clearCSRFToken();
  }
}
